import React, { useState, useEffect } from 'react';
import { FiPlay, FiFileText, FiUsers, FiTrendingUp, FiEdit } from 'react-icons/fi';

interface DashboardStats {
  totalVideos: number;
  totalTrendingNews: number;
  totalNews: number;
  totalGovSchemes: number;
  totalUsers: number;
  loading: boolean;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalVideos: 0,
    totalTrendingNews: 0,
    totalNews: 0,
    totalGovSchemes: 0,
    totalUsers: 0,
    loading: true
  });

  const [error, setError] = useState<string>('');

  // Fetch user count using a simple database query
  const fetchUserCount = async () => {
    try {
      // Add cache-busting parameter to ensure fresh data
      const cacheBuster = new Date().getTime();
      const response = await fetch(`https://mandilive.in/api/endpoints/user_count.php?_cb=${cacheBuster}`, {
        method: 'GET'
        // Removed cache headers to avoid CORS preflight issues
      }).catch(() => null);
      
      if (response && response.ok) {
        const data = await response.json();
        console.log('User count response:', data); // Debug log
        
        // The API returns {success: true, count: 1, message: "..."}
        if (data.success && typeof data.count === 'number') {
          console.log('User count extracted:', data.count);
          return data.count;
        } else {
          console.warn('Invalid user count response structure:', data);
          return 0;
        }
      } else {
        console.warn('User count endpoint failed. Status:', response?.status);
      }
      
      return 0;
    } catch (err) {
      console.error('Failed to fetch user count:', err);
      return 0;
    }
  };

  // Fetch dashboard stats from various APIs
  const fetchDashboardStats = async () => {
    setError('');
    setStats(prev => ({ ...prev, loading: true }));
    try {
      const baseUrl = 'https://mandilive.in/api';
      
      // Fetch all data in parallel
      const [videosRes, trendingNewsRes, newsRes, govSchemesRes, userCount] = await Promise.all([
        fetch(`${baseUrl}/videos`).catch(() => null),
        fetch(`https://mandilive.in/api/endpoints/trending_news.php`).catch(() => null),
        fetch(`${baseUrl}/news`).catch(() => null),
        fetch(`${baseUrl}/government-schemes`).catch(() => null),
        fetchUserCount()
      ]);
      
      let totalVideos = 0;
      let totalTrendingNews = 0;
      let totalNews = 0;
      let totalGovSchemes = 0;
      
      // Process videos response
      if (videosRes && videosRes.ok) {
        const videosData = await videosRes.json();
        totalVideos = videosData.total || videosData.count || (Array.isArray(videosData.data) ? videosData.data.length : 0);
      }
      
      // Process trending news response
      if (trendingNewsRes && trendingNewsRes.ok) {
        const trendingData = await trendingNewsRes.json();
        totalTrendingNews = Array.isArray(trendingData) ? trendingData.length : (trendingData.total || trendingData.count || 0);
      }
      
      // Process news response
      if (newsRes && newsRes.ok) {
        const newsData = await newsRes.json();
        totalNews = newsData.total || newsData.count || (Array.isArray(newsData.data) ? newsData.data.length : 0);
      }
      
      // Process government schemes response
      if (govSchemesRes && govSchemesRes.ok) {
        const govData = await govSchemesRes.json();
        totalGovSchemes = govData.total || govData.count || (Array.isArray(govData.data) ? govData.data.length : 0);
      }
      
      setStats({
        totalVideos,
        totalTrendingNews,
        totalNews,
        totalGovSchemes,
        totalUsers: userCount,
        loading: false
      });
      
    } catch (err: any) {
      console.error('Error fetching dashboard stats:', err);
      setError('Failed to load dashboard statistics');
      setStats(prev => ({ ...prev, loading: false }));
    }
  };
  
  useEffect(() => {
    fetchDashboardStats();
  }, []);

  // Dashboard cards configuration
  const dashboardCards = [
    {
      title: 'Total Mandi Bhav Videos',
      value: stats.loading ? '...' : stats.totalVideos.toLocaleString(),
      icon: FiPlay,
      color: 'blue'
    },
    {
      title: 'Total Trending News',
      value: stats.loading ? '...' : stats.totalTrendingNews.toLocaleString(),
      icon: FiTrendingUp,
      color: 'green'
    },
    {
      title: 'Total News Articles',
      value: stats.loading ? '...' : stats.totalNews.toLocaleString(),
      icon: FiEdit,
      color: 'blue'
    },
    {
      title: 'Total Gov. Schemes',
      value: stats.loading ? '...' : stats.totalGovSchemes.toLocaleString(),
      icon: FiFileText,
      color: 'green'
    },
    {
      title: 'Total Users',
      value: stats.loading ? '...' : stats.totalUsers.toLocaleString(),
      icon: FiUsers,
      color: 'purple'
    }
  ];

  


  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
        <p className="text-gray-600 mt-1">Monitor your mandi bhav and news management system</p>
        
        {/* Refresh button */}
        <div className="mt-2">
          <button
            onClick={fetchDashboardStats}
            disabled={stats.loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            {stats.loading ? 'Loading...' : 'Refresh Data'}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {dashboardCards.map((card, index) => {
          const Icon = card.icon;
          const colorClasses = {
            blue: 'bg-blue-500 text-blue-600',
            green: 'bg-green-500 text-green-600',
            purple: 'bg-purple-500 text-purple-600',
            orange: 'bg-orange-500 text-orange-600'
          };

          return (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600">{card.title}</p>
                  <div className="flex items-center mt-2">
                    <div className={`p-2 rounded-lg ${colorClasses[card.color as keyof typeof colorClasses].replace('text-', 'bg-').replace('-600', '-100')}`}>
                      <Icon className={`h-5 w-5 ${colorClasses[card.color as keyof typeof colorClasses].split(' ')[1]}`} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex items-baseline">
                  <p className="text-2xl font-semibold text-gray-900">{card.value}</p>
                  {stats.loading && (
                    <div className="ml-2 inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
};

export default Dashboard;
